import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateLastActivity, logoutUser } from '../store/slices/authSlice';
import { useFeatureFlags } from '../config/featureFlags';
import { useAdminApiService } from './useAdminApiService';

interface UseInactivityTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  countdownSeconds?: number;
  testMode?: boolean; // For testing with shorter timeouts
  disabled?: boolean; // Temporarily disable the timeout
}

export const useInactivityTimeout = (options: UseInactivityTimeoutOptions = {}) => {
  const { flags, auth: authConfig } = useFeatureFlags();
  const { getSessionSettings, getInactivitySettings } = useAdminApiService();
  
  const {
    timeoutMinutes = authConfig.sessionTimeoutMinutes, // Use feature flag default
    warningMinutes = 2,  // Show countdown for 2 minutes
    countdownSeconds = 120, // 2 minutes countdown
    testMode = false, // For testing with shorter timeouts
    disabled = false // Temporarily disable the timeout
  } = options;

  // State for dynamic settings
  const [dynamicSettings, setDynamicSettings] = useState({
    sessionTimeoutMinutes: timeoutMinutes,
    countdownSeconds: countdownSeconds,
    inactivityTimeoutEnabled: true
  });

  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const lastActivity = useAppSelector(state => state.auth.lastActivity);

  // Debouncing refs
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityUpdateRef = useRef<number>(0);
  const isAdminPage = window.location.pathname.startsWith('/admin');
  const DEBOUNCE_DELAY = isAdminPage ? 60000 : 30000; // 60 seconds for admin, 30 seconds for regular users

  // Use shorter timeouts in test mode, but extend admin timeouts
  const actualTimeoutMinutes = testMode ? 1 : (isAdminPage ? Math.max(dynamicSettings.sessionTimeoutMinutes, 60) : dynamicSettings.sessionTimeoutMinutes);
  const actualCountdownSeconds = testMode ? 30 : (isAdminPage ? Math.max(dynamicSettings.countdownSeconds, 300) : dynamicSettings.countdownSeconds);

  const [showModal, setShowModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(actualCountdownSeconds);

  // Check if inactivity timeout is enabled for current context
  const isDisabled = disabled || !flags.enableInactivityTimeout || !dynamicSettings.inactivityTimeoutEnabled;
  
  // Manual test trigger for development
  const isTestMode = process.env.NODE_ENV === 'development' && window.location.search.includes('test-timeout=true');
  const manualTestTrigger = process.env.NODE_ENV === 'development' && window.location.search.includes('manual-test=true');
  
  // Load dynamic settings from backend
  useEffect(() => {
    // Don't load settings if hook is disabled
    if (isDisabled) {
      console.log('InactivityTimeout: Disabled - skipping settings load');
      return;
    }
    
    const loadSettings = async () => {
      try {
        console.log('InactivityTimeout: Loading dynamic settings...');
        
        // Only load admin settings if we're on an admin page
        if (isAdminPage) {
          // Load session settings for admin users (non-blocking)
          try {
            const sessionResponse = await getSessionSettings();
            if (sessionResponse.success) {
              const sessionData = sessionResponse.data;
              const currentTimeout = sessionData.adminSessionTimeoutMinutes;
              
              console.log('InactivityTimeout: Admin session settings loaded:', {
                adminTimeout: sessionData.adminSessionTimeoutMinutes,
                currentTimeout
              });
              
              setDynamicSettings(prev => ({
                ...prev,
                sessionTimeoutMinutes: currentTimeout || timeoutMinutes
              }));
            }
          } catch (error) {
            console.log('InactivityTimeout: Session settings not available, using defaults');
          }

          // Load inactivity settings for admin users (non-blocking)
          try {
            const inactivityResponse = await getInactivitySettings();
            if (inactivityResponse.success) {
              const inactivityData = inactivityResponse.data;
              console.log('InactivityTimeout: Admin inactivity settings loaded:', {
                enabled: inactivityData.inactivityTimeoutEnabled,
                countdown: inactivityData.countdownSeconds
            });
            
              setDynamicSettings(prev => ({
                ...prev,
                inactivityTimeoutEnabled: inactivityData.inactivityTimeoutEnabled !== false,
                countdownSeconds: inactivityData.countdownSeconds || countdownSeconds,
              }));
            }
          } catch (error) {
            console.log('InactivityTimeout: Inactivity settings not available, using defaults');
          }
        } else {
          // For retail users, use static settings from feature flags
          console.log('InactivityTimeout: Using static settings for retail user:', {
            timeoutMinutes,
            countdownSeconds,
            inactivityTimeoutEnabled: true
          });
          
          setDynamicSettings(prev => ({
            ...prev,
            sessionTimeoutMinutes: timeoutMinutes,
            countdownSeconds: countdownSeconds,
            inactivityTimeoutEnabled: true
          }));
        }
      } catch (error) {
        console.error('Failed to load session settings:', error);
        // Use default values if backend call fails
        setDynamicSettings(prev => ({
          ...prev,
          sessionTimeoutMinutes: timeoutMinutes,
          countdownSeconds: countdownSeconds,
          inactivityTimeoutEnabled: true
        }));
      }
    };

    if (isAuthenticated) {
      loadSettings();
      
      // Only refresh settings for admin users
      if (isAdminPage) {
        const refreshInterval = setInterval(loadSettings, 5 * 60 * 1000);
        return () => {
          clearInterval(refreshInterval);
        };
      }
    }
  }, [isAuthenticated, isAdminPage, getSessionSettings, getInactivitySettings, timeoutMinutes, countdownSeconds, isDisabled]);
  
  // Manual test trigger
  useEffect(() => {
    if (manualTestTrigger && isAuthenticated && !showModal) {
      console.log('InactivityTimeout: Manual test trigger activated');
      setTimeout(() => {
        setShowModal(true);
        setTimeRemaining(actualCountdownSeconds);
      }, 3000); // Show modal after 3 seconds
    }
  }, [manualTestTrigger, isAuthenticated, showModal, actualCountdownSeconds]);

  // Add debugging to confirm admin page detection (only in dev mode to reduce console spam)
  if (process.env.NODE_ENV === 'development') {
    console.log('useInactivityTimeout: Admin page detection:', { 
      isAdminPage, 
      isDisabled, 
      disabled,
      pathname: window.location.pathname,
      enableInactivityTimeout: flags.enableInactivityTimeout,
      inactivityTimeoutEnabled: dynamicSettings.inactivityTimeoutEnabled,
      sessionTimeoutMinutes: dynamicSettings.sessionTimeoutMinutes
    });
  }

  // Activity detection function with improved debouncing
  const resetInactivityTimer = useCallback(() => {
    if (!isAuthenticated || isDisabled) return;
    
    // Don't reset timer if modal is open - let user make their choice
    if (showModal) return;
    
    // Check if any modal is open by looking for modal elements
    const modalElements = document.querySelectorAll('[role="dialog"], .modal, .modal-overlay, [data-modal]');
    if (modalElements.length > 0) {
      return; // Don't reset timer if any modal is open
    }
    
    // Check if there are any active operations (KYT analysis, tracing, etc.)
    const progressElements = document.querySelectorAll('[data-progress], .progress-indicator, .loading-indicator');
    if (progressElements.length > 0) {
      return; // Don't reset timer if there are active operations
    }
    
    // Check if there are any active API calls by looking for loading states
    const loadingElements = document.querySelectorAll('.loading, .spinner, [aria-busy="true"], [data-loading="true"]');
    if (loadingElements.length > 0) {
      return; // Don't reset timer if there are loading operations
    }
    
    // Be much less aggressive in admin contexts to prevent excessive re-renders
    if (isAdminPage) {
      // Only update activity every 10 minutes in admin contexts to prevent excessive API calls
      const ADMIN_DEBOUNCE_DELAY = 600000; // 10 minutes
      if (Date.now() - lastActivityUpdateRef.current < ADMIN_DEBOUNCE_DELAY) {
        return;
      }
    }
    
    const now = Date.now();
    
    // Debounce activity updates - only update if enough time has passed
    if (now - lastActivityUpdateRef.current < DEBOUNCE_DELAY) {
      return;
    }
    
    // Clear any pending timeout
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    // Set a new timeout for the activity update
    activityTimeoutRef.current = setTimeout(() => {
      dispatch(updateLastActivity());
      lastActivityUpdateRef.current = Date.now();
      setShowModal(false);
      setTimeRemaining(actualCountdownSeconds);
    }, DEBOUNCE_DELAY);
  }, [isAuthenticated, dispatch, actualCountdownSeconds, showModal, isAdminPage, isDisabled]);

  // Check for inactivity
  const checkInactivity = useCallback(() => {
    if (!isAuthenticated || isDisabled) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;
    const inactivityThreshold = actualTimeoutMinutes * 60 * 1000; // Convert to milliseconds

    // Add debugging for inactivity calculations
    console.log('InactivityTimeout: Checking inactivity:', {
      now: new Date(now).toISOString(),
      lastActivity: new Date(lastActivity).toISOString(),
      timeSinceLastActivity: Math.round(timeSinceLastActivity / 1000 / 60) + ' minutes',
      inactivityThreshold: Math.round(inactivityThreshold / 1000 / 60) + ' minutes',
      showModal
    });

    if (timeSinceLastActivity >= inactivityThreshold && !showModal) {
      console.log(`InactivityTimeout: User inactive for ${actualTimeoutMinutes} minutes, showing warning modal`);
      setShowModal(true);
      setTimeRemaining(actualCountdownSeconds);
    }
  }, [isAuthenticated, lastActivity, actualTimeoutMinutes, showModal, actualCountdownSeconds, isDisabled]);

  // Set up activity event listeners
  useEffect(() => {
    if (isDisabled) {
      console.log('InactivityTimeout: Disabled - user not authenticated or hook disabled');
      return;
    }

    if (!isAuthenticated) {
      setShowModal(false);
      // Clear any pending activity timeout
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
        activityTimeoutRef.current = null;
      }
      return;
    }

    console.log('InactivityTimeout: Setting up activity listeners for', isAdminPage ? 'admin' : 'retail', 'pages');

    const activityEvents = [
      'mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click',
      'focus', 'blur', 'visibilitychange'
    ];

    const handleActivity = (event: Event) => {
      // Don't trigger activity for form interactions that might cause data loss
      const target = event.target;
      
      // Check if target is an Element and has the closest method
      if (target && target instanceof Element) {
        const element = target as Element;
        if (element.tagName === 'INPUT' || 
            element.tagName === 'TEXTAREA' || 
            element.tagName === 'SELECT' ||
            element.closest('input') ||
            element.closest('textarea') ||
            element.closest('select')
        ) {
          // For form elements, only trigger on focus/blur, not on every keystroke
          if (event.type === 'focus' || event.type === 'blur') {
            resetInactivityTimer();
          }
          // Skip keypress events for form elements to prevent focus loss
        } else {
          // For non-form elements, trigger normally
          resetInactivityTimer();
        }
      } else {
        // If target is not an Element, trigger activity normally
        resetInactivityTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Check for inactivity every 5 minutes (or every 30 seconds in test mode)
    const checkInterval = testMode ? 30000 : (isAdminPage ? 300000 : 600000); // 5 minutes for admin, 10 minutes for regular users, 30 seconds in test
    const inactivityCheckInterval = setInterval(checkInactivity, checkInterval);

    // Initial check
    checkInactivity();

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearInterval(inactivityCheckInterval);
      // Clear any pending activity timeout
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
        activityTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated, resetInactivityTimer, checkInactivity, testMode, isDisabled]);

  // Handle modal close
  const handleContinue = useCallback(() => {
    if (isDisabled) return;
    
    console.log('InactivityTimeout: User chose to continue session');
    // Update last activity when user chooses to continue
    dispatch(updateLastActivity());
    lastActivityUpdateRef.current = Date.now();
    setShowModal(false);
    setTimeRemaining(actualCountdownSeconds);
    
    // Don't trigger additional activity updates that might cause re-renders
    // The activity update above is sufficient
  }, [dispatch, actualCountdownSeconds, isDisabled]);

  const handleLogout = useCallback(() => {
    if (isDisabled) return;
    
    console.log('InactivityTimeout: User chose to logout or session expired');
    setShowModal(false);
    dispatch(logoutUser()); // <-- This line logs the user out!
  }, [isDisabled, dispatch]);

  // Return early if disabled
  if (isDisabled) {
    return {
      showModal: false,
      timeRemaining: 0,
      handleContinue: () => {},
      handleLogout: () => {}
    };
  }

  return {
    showModal,
    timeRemaining,
    handleContinue,
    handleLogout,
    // Debug information
    debug: {
      isDisabled,
      isAuthenticated,
      lastActivity: new Date(lastActivity).toISOString(),
      actualTimeoutMinutes,
      actualCountdownSeconds,
      dynamicSettings,
      isAdminPage,
      flags: {
        enableInactivityTimeout: flags.enableInactivityTimeout,
        inactivityTimeoutEnabled: dynamicSettings.inactivityTimeoutEnabled
      }
    }
  };
}; 